import React, { useState, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { toast } from 'react-toastify';
import useDrawingsApi from '../hooks/useDrawingsApi';

// PDF.js worker setup using bundled entry
// Use PDF.js worker from CDN for consistent loading
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

/**
 * Table to review drawing pages needing human approval.
 * Props:
 * - pages: array of page objects with needsReview === true
 * - onApprove(pageId): callback to approve one page
 * - onApproveAll(): callback to approve all pages
 */
export default function ReviewDrawingsTable({ bidId, pages, onApprove, onApproveAll }) {
  const { downloadPage, updatePageMeta } = useDrawingsApi();
  const [urls, setUrls] = useState({});
  const [localPages, setLocalPages] = useState(pages);

  // Sync localPages when prop pages change
  useEffect(() => {
    setLocalPages(pages);
  }, [pages]);

  // Fetch presigned URLs for page PDFs
  useEffect(() => {
    pages.forEach(page => {
      if (!urls[page.id]) {
        downloadPage(bidId, page.id)
          .then(url => setUrls(prev => ({ ...prev, [page.id]: url })))
          .catch(err => console.error('Failed to download page URL', err));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages, downloadPage]);

  const handleFieldChange = (pageId, field, value) => {
    setLocalPages(prev => prev.map(p => p.id === pageId ? { ...p, [field]: value } : p));
  };

  const handleFieldBlur = async (pageId, field) => {
    const page = localPages.find(p => p.id === pageId);
    if (!page) return;
    try {
      await updatePageMeta(bidId, page.id, { [field]: page[field] });
      toast.success('Metadata updated');
    } catch (err) {
      console.error(err);
      toast.error('Failed to update metadata');
    }
  };

  const drawingTypes = [
    'Architectural', 'Structural Steel', 'Mechanical',
    'Electrical', 'Civil', 'Landscape', 'Miscellaneous'
  ];

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-2">Review Drawings</h2>
      <button
        onClick={onApproveAll}
        className="mb-4 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
      >
        Approve All ({pages.length})
      </button>
      <table className="w-full table-auto border-collapse">
        <thead className="bg-gray-100">
          <tr>
            <th className="border p-2">Preview</th>
            <th className="border p-2">Number</th>
            <th className="border p-2">Title</th>
            <th className="border p-2">Type</th>
            <th className="border p-2">Rev Date</th>
            <th className="border p-2">Needs Review</th>
            <th className="border p-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {localPages.map(page => (
            <tr key={page.id} className="hover:bg-gray-50">
              <td className="border p-2">
                {urls[page.id] && (
                  <Document file={urls[page.id]}>
                    <Page pageNumber={1} width={120} />
                  </Document>
                )}
              </td>
              <td className="border p-2">
                <input
                  type="text"
                  className="w-full p-1 border rounded"
                  value={page.drawingNumber || ''}
                  onChange={e => handleFieldChange(page.id, 'drawingNumber', e.target.value)}
                  onBlur={() => handleFieldBlur(page.id, 'drawingNumber')}
                />
              </td>
              <td className="border p-2">
                <input
                  type="text"
                  className="w-full p-1 border rounded"
                  value={page.drawingTitle || ''}
                  onChange={e => handleFieldChange(page.id, 'drawingTitle', e.target.value)}
                  onBlur={() => handleFieldBlur(page.id, 'drawingTitle')}
                />
              </td>
              <td className="border p-2">
                <select
                  className="w-full p-1 border rounded"
                  value={page.drawingType || ''}
                  onChange={e => handleFieldChange(page.id, 'drawingType', e.target.value)}
                  onBlur={() => handleFieldBlur(page.id, 'drawingType')}
                >
                  {drawingTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </td>
              <td className="border p-2">
                <input
                  type="text"
                  className="w-full p-1 border rounded"
                  value={page.drawingRevDate || ''}
                  onChange={e => handleFieldChange(page.id, 'drawingRevDate', e.target.value)}
                  onBlur={() => handleFieldBlur(page.id, 'drawingRevDate')}
                />
              </td>
              <td className="border p-2 text-center">{page.needsReview ? 'Yes' : 'No'}</td>
              <td className="border p-2 text-center">
                <button
                  onClick={() => onApprove(page.id)}
                  className="bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
                >
                  Approve
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}